import axios from 'axios';

// Configuración base de axios
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

console.log('API_BASE_URL:', API_BASE_URL); // Para debug

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 75000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar token de autenticación LTI
api.interceptors.request.use(
  (config) => {
    // Solo token LTI (desde Canvas)
    const ltiToken = localStorage.getItem('lti_token');
    if (ltiToken) {
      config.headers.Authorization = `Bearer ${ltiToken}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejo de errores
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);

    // Si hay error 401, limpiar token LTI
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('lti_token');
      localStorage.removeItem('lti_user');
      // En contexto LTI, mostrar mensaje de error pero no redirigir
      console.error('Sesión LTI expirada o inválida');
    }

    return Promise.reject(error);
  }
);

// Exportar la instancia de axios para uso directo
export { api };

// Servicios de Canvas API (exclusivamente)
export const canvasAPI = {
  // === Endpoints para Estudiantes ===

  // Obtener mi certificado
  getMyCertificate: async () => {
    const response = await api.get('/canvas/student/certificate');
    return response.data;
  },

  // Obtener TODOS mis certificados
  getAllMyCertificates: async () => {
    const response = await api.get('/canvas/student/all-certificates');
    return response.data;
  },


  // Descargar mi certificado
  downloadMyCertificate: async () => {
    const response = await api.get('/canvas/student/certificate/download', {
      responseType: 'blob',
    });

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'mi_certificado.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  // Descargar certificado por ID (estudiante)
  downloadStudentCertificateById: async (certificateId, certificateName) => {
    const response = await api.get(`/canvas/student/certificates/${certificateId}/download`, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${certificateName || 'certificado'}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  // === Endpoints para Instructores ===

  // Obtener estudiantes del curso con calificaciones
  getCourseStudents: async () => {
    const response = await api.get('/canvas/instructor/students');
    return response.data;
  },

  // Obtener configuración institucional (vista instructor)
  getInstructorSettings: async () => {
    const response = await api.get('/canvas/instructor/settings');
    return response.data;
  },

  // Obtener assignments (evaluaciones) del curso
  getCourseAssignments: async () => {
    const response = await api.get('/canvas/instructor/assignments');
    return response.data;
  },

  // Sincronizar calificaciones
  syncGrades: async () => {
    const response = await api.post('/canvas/instructor/sync-grades');
    return response.data;
  },

  // Evaluar estudiantes por criterios
  evaluateStudents: async (criteria) => {
    const response = await api.post('/canvas/instructor/evaluate-students', { criteria });
    return response.data;
  },

  // Crear certificados masivos (ASYNC - devuelve job_id)
  bulkCreateCertificates: async (studentIds, courseName, duration, templateId = null, customCourseValues = null) => {
    const requestBody = {
      student_ids: studentIds,
      course_name: courseName,
      duration: duration,
    };

    if (templateId) {
      requestBody.template_id = templateId;
    }

    if (customCourseValues) {
      requestBody.custom_course_values = customCourseValues;
    }

    const response = await api.post('/canvas/instructor/certificates/bulk-create', requestBody);
    return response.data;
  },

  // Consultar estado de job de certificados
  getCertificateJobStatus: async (jobId) => {
    const response = await api.get(`/canvas/instructor/certificates/job/${jobId}`);
    return response.data;
  },

  // Obtener certificados del curso
  getCourseCertificates: async (includeRevoked = false) => {
    const response = await api.get('/canvas/instructor/certificates', {
      params: { include_revoked: includeRevoked },
    });
    return response.data;
  },

  // Revocar certificado
  revokeCertificate: async (certificateId, reason = null) => {
    const response = await api.delete(`/canvas/instructor/certificates/${certificateId}`, {
      params: { reason },
    });
    return response.data;
  },

  // Descargar certificado (instructor)
  downloadCertificateByInstructor: async (certificateId, certificateName) => {
    const response = await api.get(`/canvas/instructor/certificates/${certificateId}/download`, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${certificateName || 'certificado'}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  // Descargar múltiples certificados (instructor)
  downloadMultipleCertificates: async (certificateIds) => {
    const response = await api.post(
      '/canvas/instructor/certificates/download-multiple',
      { certificate_ids: certificateIds },
      { responseType: 'blob' }
    );

    // Extraer nombre del archivo del header Content-Disposition
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'certificados.zip';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/i);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    const blob = new Blob([response.data], { type: 'application/zip' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  // === Endpoints de Firmas ===

  // Subir firma
  uploadSignature: async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/canvas/signature/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Obtener mi firma
  getMySignature: async () => {
    const response = await api.get('/canvas/signature/my-signature');
    return response.data;
  },

  // === Endpoints de Administración ===

  // Obtener todos los certificados (admin)
  getAllCertificates: async (filters = {}) => {
    const response = await api.get('/canvas/admin/certificates', {
      params: filters,
    });
    return response.data;
  },

  // Descargar múltiples certificados (admin)
  downloadMultipleCertificatesByAdmin: async (certificateIds) => {
    const response = await api.post(
      '/canvas/admin/certificates/download-multiple',
      { certificate_ids: certificateIds },
      { responseType: 'blob' }
    );

    // Extraer nombre del archivo del header Content-Disposition
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'certificados.zip';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/i);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    const blob = new Blob([response.data], { type: 'application/zip' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  // Descargar certificado individual (admin)
  downloadCertificateByAdmin: async (certificateId, certificateName) => {
    const response = await api.get(`/canvas/admin/certificates/${certificateId}/download`, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${certificateName || 'certificado'}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  // Obtener configuración institucional
  getInstitutionSettings: async () => {
    const response = await api.get('/canvas/admin/settings');
    return response.data;
  },

  // Actualizar configuración institucional
  updateInstitutionSettings: async (settings) => {
    const response = await api.put('/canvas/admin/settings', settings);
    return response.data;
  },

  // Subir firma director
  uploadDirectorSignature: async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/canvas/admin/director-signature', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Subir logo institucional
  uploadInstitutionLogo: async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/canvas/admin/institution-logo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Actualizar plantilla
  updateCertificateTemplate: async (content) => {
    const response = await api.put('/canvas/admin/template', { content });
    return response.data;
  },

  // Previsualizar plantilla
  previewCertificateTemplate: async (content) => {
    const response = await api.post('/canvas/admin/template/preview',
      { content },
      { responseType: 'blob' }
    );
    return response;
  },

  // Agregar variable personalizada
  addCustomVariable: async (variable) => {
    const response = await api.post('/canvas/admin/custom-variables', { variable });
    return response.data;
  },

  // Actualizar variable personalizada
  updateCustomVariable: async (variableName, variable) => {
    const response = await api.put(`/canvas/admin/custom-variables/${variableName}`, { variable });
    return response.data;
  },

  // Eliminar variable personalizada
  deleteCustomVariable: async (variableName) => {
    const response = await api.delete(`/canvas/admin/custom-variables/${variableName}`);
    return response.data;
  },

  // Obtener lista de imágenes disponibles
  getAvailableImages: async () => {
    const response = await api.get('/canvas/admin/images');
    return response.data;
  },

  // Subir imagen personalizada
  uploadCustomImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/canvas/admin/images', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Eliminar imagen personalizada
  deleteCustomImage: async (filename) => {
    const response = await api.delete(`/canvas/admin/images/${filename}`);
    return response.data;
  },

  // Obtener URL de miniatura de imagen
  getImageThumbnailUrl: (filename) => {
    return `${API_BASE_URL}/canvas/admin/images/${filename}/thumbnail`;
  },

  // Obtener logs de auditoría
  getAuditLogs: async (filters = {}) => {
    const response = await api.get('/canvas/admin/audit-logs', {
      params: filters,
    });
    return response.data;
  },

  // === Gestión de Plantillas de Certificados ===

  // Obtener todas las plantillas (Admin)
  getTemplates: async () => {
    const response = await api.get('/canvas/admin/templates');
    return response.data;
  },

  // Obtener plantilla por ID (Admin)
  getTemplateById: async (templateId) => {
    const response = await api.get(`/canvas/admin/templates/${templateId}`);
    return response.data;
  },

  // Crear nueva plantilla (Admin)
  createTemplate: async (templateData) => {
    const response = await api.post('/canvas/admin/templates', templateData);
    return response.data;
  },

  // Actualizar plantilla existente (Admin)
  updateTemplate: async (templateId, templateData) => {
    const response = await api.put(`/canvas/admin/templates/${templateId}`, templateData);
    return response.data;
  },

  // Eliminar plantilla (Admin)
  deleteTemplate: async (templateId) => {
    const response = await api.delete(`/canvas/admin/templates/${templateId}`);
    return response.data;
  },

  // Obtener plantillas disponibles para instructor
  getInstructorTemplates: async () => {
    const response = await api.get('/canvas/instructor/templates');
    return response.data;
  },
};

export default api;
